import { useCallback } from "react";

// Import reusable components
import { useUppyDropzoneCore } from "@/hooks/imageManager/useUppyDropzoneCore";
import { Spinner } from "../ui/Spinner";

export default function CustomDropzoneForm({
  value = [],
  onChange,
  setError,
  clearErrors,
  dropzoneRef,
  onSelectExistingClick,
  showSelectExistingButton = true,
  attachUploader,
}) {
  // Memoize the onFilesProcessed callback
  const handleFilesProcessed = useCallback(
    (newImages) => {
      const formatted = newImages.map((img) => ({
        id: crypto.randomUUID(),
        // Images produced by the manager expose `url` and `alt` fields
        url: img.url,
        alt: img.alt,
        file: null,
      }));
      onChange([...value, ...formatted]);
    },
    [onChange, value]
  );

  // Memoize the existingAlts array
  const existingAlts = useCallback(() => value.map((img) => img.alt), [value]);

  const { getRootProps, getInputProps, isDragActive, isUploading, uploadingCount } =
    useUppyDropzoneCore({
      onFilesProcessed: handleFilesProcessed,
      existingAlts: existingAlts(),
      setError,
      clearErrors,
      dropzoneRef,
      exposeOpen: true,
      attachUploader,
    });

  return (
    <>
      <input {...getInputProps()} style={{ display: "none" }} />

      <div
        {...getRootProps({
          onClick: (e) => {
            if (e.currentTarget !== e.target) {
              e.preventDefault();
              e.stopPropagation();
            }
          },
        })}
        className={`relative w-full border-2 border-dashed rounded-lg px-6 py-10 flex flex-col items-center justify-center text-center cursor-pointer transition 
          ${isDragActive ? "bg-[#f0f0f0] border-[#aaa]" : "bg-[#fcfcfc] border-[#c7c7c7]"}`}
      >
        <p className="mt-2 text-[14px] font-medium text-[#666]">
          {isUploading
            ? `Uploading ${uploadingCount} image${uploadingCount > 1 ? "s" : ""}...`
            : "Drag and drop PNG, JPEG, JPG images"}
        </p>

        <p className="text-xs mt-1 text-[#999]">Max size: 2MB per image</p>

        {isUploading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] rounded-lg flex items-center justify-center">
            <Spinner size="small">
              <p className="text-sm text-[#444] mt-2"></p>
            </Spinner>
          </div>
        )}

      </div>

      <div
        onClick={(e) => e.stopPropagation()}
        className="flex gap-2 items-center justify-center mt-2"
      >
        <button
          type="button"
          onClick={() => dropzoneRef?.current?.()}
          className="text-sm font-medium shadow-sm bg-white border border-[#a0a0a0] rounded-md px-[10px] py-[5px] hover:bg-[#f3f3f3] transition"
        >
          Upload new
        </button>

        {showSelectExistingButton && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSelectExistingClick?.();
            }}
            className="text-sm font-medium shadow-sm bg-white border border-[#a0a0a0] rounded-md px-[10px] py-[5px] hover:bg-[#f3f3f3] transition"
          >
            Select existing
          </button>
        )}
      </div>
    </>
  );
}
